import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Observable, Subject, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'app-countdown-ring',
    standalone: true,
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './countdown-ring.component.html',
})
export class CountdownRingComponent implements OnInit, OnDestroy {
    /**
     * Total time in milliseconds for the countdown.
     */
    @Input({ required: true }) totalMs!: number;

    /**
     * An observable that emits the remaining time in milliseconds.
     */
    @Input({ required: true }) remainingMs$!: Observable<number>;
    remainingMs = 0;

    /**
     * An observable that emits a value between 0 and 1 representing the percentage of time remaining.
     */
    @Input({ required: true }) percentageRemaining$!: Observable<number>;
    percentageRemaining = 1;

    /**
     * A function that formats the remaining time in milliseconds into a string.
     */
    @Input({ required: true }) formatTimeRemainingFn!: (ms: number) => string;

    /** Ring config (SVG units). */
    readonly size = 120;
    readonly center = 60;
    readonly radius = 54;
    readonly circumference = 2 * Math.PI * this.radius;

    /**
     * The stroke-dashoffset attribute is a presentation attribute defining an offset on the rendering of the associated dash array.
     * See https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke-dashoffset.
     */
    circleStrokeDashOffset = 0;

    private destroy$ = new Subject<void>();

    constructor() {}

    /**
     * The `stroke-dasharray` attribute is a presentation attribute defining the pattern of dashes and gaps used to paint the outline of the shape.
     * See https://developer.mozilla.org/en-US/docs/Web/SVG/Reference/Attribute/stroke-dasharray.
     */
    get circleStrokeDasharray(): string {
        return `${this.circumference} ${this.circumference}`;
    }

    ngOnInit(): void {
        this.percentageRemainingValueChangesHandler();
        this.remainingMsValueChangesHandler();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    formatRemaining(): string {
        return this.formatTimeRemainingFn(this.remainingMs);
    }

    private percentageRemainingValueChangesHandler(): void {
        this.percentageRemaining$
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => {
                    this.percentageRemaining = value;
                    this.circleStrokeDashOffset =
                        this.circumference * (1 - value);
                }),
            )
            .subscribe();
    }

    private remainingMsValueChangesHandler(): void {
        this.remainingMs$
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => {
                    this.remainingMs = value;
                }),
            )
            .subscribe();
    }
}
